import {useContext, useRef, useState} from 'react'

import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomSkeleton, {ATOM_SKELETON_VARIANTS} from '@s-ui/react-atom-skeleton'
import Context from '@s-ui/react-context'
import useMount from '@s-ui/react-hooks/lib/useMount'
import MoleculeModal from '@s-ui/react-molecule-modal'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import {useDateFormat} from '@adv-mt/mt-hook-use-date-format'
import ModalBasicContent from '@adv-mt/mt-modal-basic-content'
import {CardAdSkeleton, ModalCardVehicle} from '@adv-mt/mt-panel-vehicle-reservation'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconInfo from '@adv-mt/mt-svg-icons/lib/InfoOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {useMessagingContext} from '../../useMessagingContext/index.js'

const skeletonProps = {
  ANIMATION: 'pulse',
  HEIGHT: '86px',
  WIDTH: '530px'
}
const RESERVATION_DETAILED_CLICKED = 'Reservation Detailed Clicked'

export const ReservationInfoTransactionModal = () => {
  const {domain, i18n} = useContext(Context)
  const {setInteractionComponent, state} = useMessagingContext()
  const {track} = useAnalyticsTrack()
  const [reservation, setReservation] = useState()
  const [adDetails, setAdDetails] = useState()

  const currentState = useRef(null)
  currentState.current = state

  const {interactionUrl} = currentState.current
  const {searchParams} = new URL(interactionUrl)
  const reservationId = searchParams.get('reservationId')
  const adId = searchParams.get('adId')

  const validatedDateReservation = useDateFormat(new Date(reservation?.createdAt))
  const expirationDateReservation = useDateFormat(new Date(reservation?.expirationDate))

  const getAmount = (amount, i18n) => {
    return isNaN(amount) ? '' : i18n.c(amount)
  }

  useMount(async () => {
    const [reservationResult, adDetailsResult] = await Promise.all([
      domain.get('get_reservation_by_id_transactions_use_case').execute({reservationId}),
      domain.get('get_ad_details_ads_use_case').execute({adId})
    ])

    const [errorReservation, responseReservation] = reservationResult
    const [errorAd, responseAd] = adDetailsResult

    if (!errorReservation) {
      setReservation(responseReservation.value)
    }

    if (!errorAd) {
      setAdDetails(responseAd)
    }

    track({
      event: RESERVATION_DETAILED_CLICKED,
      data: {
        products: [{id: adId, category: 'ads', price: 0, quantity: 0}]
      }
    })
  })

  return (
    <MoleculeModal
      isOpen
      closeOnOutsideClick
      closeOnEscKeyDown
      iconClose={<IconClose size={ATOM_ICON_SIZES.medium} />}
      onClose={() => setInteractionComponent({component: null, show: false})}
    >
      <div className="mt-ReservationInfoModal-modalContainer">
        <ModalBasicContent
          stepsContent={[
            {
              buttons: {
                confirmButton: {
                  customCallBack: () => {
                    setInteractionComponent({component: null, show: false})
                  },
                  text: i18n.t('COMMON.UNDERSTOOD')
                }
              },
              children: (
                <div className="mt-ReservationInfoModal-modalChildren">
                  <div className="mt-ReservationInfoModal-description">
                    <div className="mt-ReservationInfoModal-descriptionInfoDateValid">
                      <div className="mt-ReservationInfoModal-descriptionInfoDateValid-item">
                        <div>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.RESERVATION_FROM')}</div>
                        <div>
                          <strong>{validatedDateReservation?.numeric}</strong>
                        </div>
                      </div>
                      <div className="mt-ReservationInfoModal-descriptionInfoDateValid-item">
                        <div>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.TEXT_VALID')}</div>
                        <div>
                          <strong>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.SUBTITLE_VALID')}</strong>
                        </div>
                      </div>
                      <div className="mt-ReservationInfoModal-descriptionInfoDateValid-item">
                        <div>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.RESERVATION_UNTIL')}</div>
                        <div>
                          <strong>{`${expirationDateReservation?.numeric}`}</strong>
                        </div>
                      </div>
                      <div className="mt-ReservationInfoModal-descriptionInfoDateValid-disclamer">
                        {i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.DISCLAIMER')}
                      </div>
                      <div className="mt-ReservationInfoModal-descriptionInfoDateValid-cost">
                        <div className="mt-ReservationInfoModal-descriptionInfoDateValid-costText">
                          <div>
                            <strong>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.TEXT_COST')}</strong>
                          </div>
                        </div>
                        <div className="mt-ReservationInfoModal-descriptionInfoDateValid-costAmount">
                          <div>
                            <strong>{getAmount(reservation?.amount, i18n)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-ReservationInfoModal-notification">
                    {reservation?.amount ? (
                      <MoleculeNotification
                        alignItems={moleculeNotificationAlignItems.FLEX_START}
                        autoClose={null}
                        icon={<IconInfo size="medium" />}
                        position="relative"
                        roundedCorners={moleculeNotificationBorderSizes.medium}
                        show
                        showCloseButton={false}
                        showLeftIconMobile
                        type={moleculeNotificationTypes.system}
                        variation={moleculeNotificationVariations.positive}
                      >
                        {i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.NOTIFICATION_TEXT', {
                          amount: reservation?.amount
                        })}
                      </MoleculeNotification>
                    ) : (
                      <AtomSkeleton
                        variant={ATOM_SKELETON_VARIANTS.square}
                        height={skeletonProps.HEIGHT}
                        width={skeletonProps.WIDTH}
                        animation={skeletonProps.ANIMATION}
                      />
                    )}
                  </div>
                  {adDetails ? (
                    <ModalCardVehicle
                      ad={adDetails?.ad}
                      dealer={adDetails?.seller?.dealer}
                      financingData={adDetails?.financingData}
                      isReserved
                      showContact
                    />
                  ) : (
                    <CardAdSkeleton />
                  )}
                </div>
              ),
              title: i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.TITLE'),
              titleMarginBottom: 'l'
            }
          ]}
        />
      </div>
    </MoleculeModal>
  )
}
